import PropTypes from 'prop-types';
import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations, useLocale } from 'next-intl';

import StyledPagination from './pagination.style';
import { ArrowRight, ArrowLeft } from '../../icons/arrow/arrow';
import { buildFilterQuery } from '../../../helper/api';
import { removeTrailingSlash } from '../../../helper';

const Pagination = props => {
  const {
    slug,
    currentPage,
    tokenId,
    totalPages,
    inverted = false,
    pageType = 'overview',
    customLink,
    customClick,
    noPadding = false,
    filters,
    align = 'center',
    archiveListUrls
  } = props;
  const router = useRouter();
  const t = useTranslations();
  const locale = useLocale();

  const newQuery = router?.asPath.split('?');

  const NextLink = (i, anchorProps, innerContent) => {
    if (customClick) {
      return <a {...anchorProps}>{innerContent}</a>;
    }

    if (customLink) {
      return (
        <Link href={`${customLink[i - 1]}`} {...anchorProps}>
          {innerContent}
        </Link>
      );
    }

    let query = {};
    let asPath = '';
    const uri = removeTrailingSlash(router?.asPath?.split('?')?.[0] || '');

    const queryString = filters
      ? buildFilterQuery(
          filters,
          i,
          pageType === 'time' || pageType === 'index' ? 'filters' : 'tags'
        )
      : '';

    let pageTemplate = '';

    if (pageType === 'time') {
      query = { slug, lang: locale, page: i, filters };
      asPath = `${uri}${queryString}`;
      pageTemplate = 'time';
    } else if (pageType === 'token') {
      query = { slug, lang: locale, page: i, tokenId };
      asPath = `${uri}${i !== 1 ? `?page=${i}` : ''}`;
      pageTemplate = 'token';
    } else if (pageType === 'index') {
      query = { lang: locale, page: i, filters, slug: 'index' };
      asPath = `${uri}${queryString}`;
      pageTemplate = 'time';
    } else if (pageType === 'curatedExhibition') {
      // query = { slug, lang: locale, page: i, tokenId };
      query = { uri: `${uri}?page=${i}` };
      asPath = `${uri}?page=${i}`;
      pageTemplate = 'universal';
    } else if (pageType === 'archive') {
      query = { lang: locale, page: i, filters, slug: 'index' };
      asPath = `/${archiveListUrls[i - 1]}?${newQuery[1]}page=${i}`;
      pageTemplate = 'archive';
    } else {
      query = { uri, page: i, filters, lang: locale };
      asPath = `${uri}${queryString}`;
      pageTemplate = 'universal';
    }

    return (
      <Link
        href={{
          pathname: `/${pageTemplate}`,
          query
        }}
        as={asPath}
        {...anchorProps}
      >
        {innerContent}
      </Link>
    );
  };

  const AnchorLink = (i, children = i) => {
    const anchorProps = {
      'aria-label':
        currentPage === i ? `${t('pagination.currentPage')} ${i}` : `${t('pagination.goTo')} ${i}`,
      'aria-current': currentPage === i,
      className: 'pagination__link',
      onClick: customClick,
      onKeyPress: customClick,
      role: 'link',
      tabIndex: '0',
      'data-page-index': i
    };

    const innerContent = (
      <div className="pagination__link-inner">
        <span>{children}</span>
      </div>
    );

    return NextLink(i, anchorProps, innerContent);
  };

  const renderPaginationButtons = () => {
    const links = [];
    const startIndex = currentPage >= 2 ? currentPage - 1 : currentPage;

    for (let i = startIndex; i <= totalPages && i <= currentPage + 1; i += 1) {
      links.push(<li key={i}>{AnchorLink(i)}</li>);
    }

    return links;
  };

  return (
    totalPages > 1 && (
      <StyledPagination {...props} inverted={inverted} noPadding={noPadding} align={align}>
        <ul>
          {currentPage !== 1 && (
            <li className="pagination__prev">{AnchorLink(currentPage - 1, <ArrowLeft />)}</li>
          )}

          {renderPaginationButtons()}

          {currentPage + 2 <= totalPages && (
            <>
              {totalPages !== 3 && currentPage + 2 < totalPages && (
                <span className="pagination__spacer">...</span>
              )}
              <li>{AnchorLink(totalPages)}</li>
            </>
          )}

          {currentPage !== totalPages && (
            <li className="pagination__next">{AnchorLink(currentPage + 1, <ArrowRight />)}</li>
          )}
        </ul>
      </StyledPagination>
    )
  );
};

Pagination.propTypes = {
  /** Current Page */
  currentPage: PropTypes.number.isRequired,
  /** Total amount of pages */
  totalPages: PropTypes.number.isRequired,
  /** Inverts the color for black backgrounds */
  inverted: PropTypes.bool,
  /** No Container Padding */
  noPadding: PropTypes.bool,
  /** Array of filters */
  filters: PropTypes.arrayOf(PropTypes.shape()),
  /** PageTemplate to use */
  pageType: PropTypes.string,
  align: PropTypes.string,
  customClick: PropTypes.func,
  customLink: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  slug: PropTypes.string,
  tokenId: PropTypes.string,
  archiveListUrls: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string
    })
  ).isRequired
};

export default Pagination;
